<template>
  <div class="choose-company-container">
    <!-- Полноэкранная загрузка при сохранении -->
    <Transition name="overlay">
      <div
        v-if="isUpdating"
        class="loading-overlay"
        role="status"
        aria-live="polite"
      >
        <div class="saving-card">
          <div class="saving-spinner"></div>

          <div class="saving-content">
            <p class="saving-title">Сохраняем компанию</p>
            <p class="saving-subtitle">
              Это займёт всего несколько секунд
            </p>
          </div>
        </div>
      </div>
    </Transition>

    <div class="company-content">
      <!-- Заголовок -->
      <header class="company-header">
        <div class="header-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M4 21V5.8C4 4.806 4.806 4 5.8 4H13V21"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M13 9H18.2C19.194 9 20 9.806 20 10.8V21"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M2.5 21H21.5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
            <path
              d="M7.5 8H9.5M7.5 12H9.5M7.5 16H9.5M16 13H17.5M16 16.5H17.5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <div class="header-text">
          <h2 class="company-title">
            {{ companyTitle }}
          </h2>

          <p class="company-description">
            {{ companyDescription }}
          </p>
        </div>
      </header>

      <!-- Поиск -->
      <div class="search-section">
        <div
          class="search-bar"
          :class="{
            focused: isSearchFocused,
            filled: hasSearchQuery
          }"
        >
          <svg
            class="search-icon"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="11"
              cy="11"
              r="7"
              stroke="currentColor"
              stroke-width="1.8"
            />
            <path
              d="M16.2 16.2L21 21"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>

          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="Введите название компании"
            autocomplete="off"
            spellcheck="false"
            aria-label="Поиск компании"
            @input="handleSearchInput"
            @focus="isSearchFocused = true"
            @blur="isSearchFocused = false"
          />

          <button
            v-if="hasSearchQuery && !isSearching"
            class="clear-search-button"
            type="button"
            aria-label="Очистить поиск"
            @mousedown.prevent
            @click="clearSearch"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M7 7L17 17M17 7L7 17"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </button>

          <div
            v-if="isSearching"
            class="search-spinner"
            aria-label="Выполняется поиск"
          ></div>
        </div>

        <p class="search-hint">
          Например: Яндекс, Сбер, VK или название вашей компании
        </p>
      </div>

      <!-- Выбранная компания -->
      <Transition name="selected-company">
        <div
          v-if="selectedCompany"
          class="selected-company-banner"
        >
          <div class="selected-company-check">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M5 12.5L9.3 16.5L19 7"
                stroke="currentColor"
                stroke-width="2.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="selected-company-info">
            <span class="selected-company-label">
              Компания выбрана
            </span>

            <span class="selected-company-name">
              {{ selectedCompany.name }}
            </span>
          </div>

          <button
            class="selected-company-remove"
            type="button"
            @click="clearSelection"
          >
            Изменить
          </button>
        </div>
      </Transition>

      <!-- Контент результатов -->
      <div class="results-container">
        <!-- Начальное состояние -->
        <Transition name="content-fade" mode="out-in">
          <div
            v-if="!hasSearchQuery"
            key="initial"
            class="initial-state"
          >
            <div class="initial-illustration">
              <div class="illustration-building building-back">
                <span></span>
                <span></span>
                <span></span>
                <span></span>
              </div>

              <div class="illustration-building building-front">
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
              </div>

              <div class="illustration-search">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    cx="10.5"
                    cy="10.5"
                    r="5.5"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                  <path
                    d="M15 15L20 20"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </div>
            </div>

            <h3 class="state-title">
              Начните вводить название
            </h3>

            <p class="state-description">
              Мы найдём компанию в каталоге HR3 и свяжем её
              с вашим профилем.
            </p>

            <button
              class="secondary-skip-button"
              type="button"
              @click="skipCompanyStep"
            >
              Пропустить этот шаг
            </button>
          </div>

          <!-- Shimmer во время поиска -->
          <div
            v-else-if="isSearching"
            key="loading"
            class="search-loading-state"
          >
            <div class="results-heading">
              <div>
                <p class="results-title">
                  Ищем компании
                </p>

                <p class="results-subtitle">
                  Проверяем каталог HR3
                </p>
              </div>

              <div class="searching-dots" aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>

            <div class="company-grid">
              <div
                v-for="index in 6"
                :key="index"
                class="company-skeleton"
              >
                <div class="skeleton-logo skeleton-block"></div>

                <div class="skeleton-content">
                  <div class="skeleton-title skeleton-block"></div>
                  <div class="skeleton-line skeleton-line-wide skeleton-block"></div>
                  <div class="skeleton-line skeleton-line-short skeleton-block"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Результаты -->
          <div
            v-else-if="searchResults.length > 0"
            key="results"
            class="search-results-state"
          >
            <div class="results-heading">
              <div>
                <p class="results-title">
                  Найденные компании
                </p>

                <p class="results-subtitle">
                  {{ resultsCountText }}
                </p>
              </div>
            </div>

            <div class="company-grid">
              <CompanyToChoose
                v-for="company in searchResults"
                :key="company.company_id"
                :name="company.name"
                :specifics="company.specifics"
                :logo-url="company.logo_url"
                :company-id="company.company_id"
                :is-selected="company.company_id === selectedCompanyId"
                :search-query="searchQuery"
                @company-clicked="toggleCompanySelection"
              />
            </div>
          </div>

          <!-- Ничего не найдено -->
          <div
            v-else
            key="empty"
            class="empty-state"
          >
            <div class="empty-illustration">
              <svg
                viewBox="0 0 96 96"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="17"
                  y="23"
                  width="47"
                  height="56"
                  rx="9"
                  class="empty-building"
                />

                <path
                  d="M30 36H40M30 47H40M30 58H40M50 36H54M50 47H54"
                  class="empty-lines"
                />

                <circle
                  cx="66"
                  cy="65"
                  r="17"
                  class="empty-search-circle"
                />

                <path
                  d="M78 77L88 87"
                  class="empty-search-line"
                />

                <path
                  d="M60 65H72"
                  class="empty-minus"
                />
              </svg>
            </div>

            <h3 class="state-title">
              Мы не нашли такую компанию
            </h3>

            <p class="state-description">
              Проверьте название или попробуйте использовать
              более короткий запрос.
            </p>

            <div class="empty-actions">
              <button
                class="retry-button"
                type="button"
                @click="focusSearch"
              >
                Изменить запрос
              </button>

              <button
                class="secondary-skip-button"
                type="button"
                @click="skipCompanyStep"
              >
                Пропустить этот шаг
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </div>

    <ErrorToast
      v-if="errorMessage"
      :message="errorMessage"
    />

    <WarningToast
      v-if="warningMessage"
      :message="warningMessage"
    />

    <Snackbar
      :message="'Данные успешно обновлены!'"
      :type="'success'"
      :show="successSnackbar"
    />
  </div>
</template>

<script setup>
import {
  computed,
  defineEmits,
  defineExpose,
  onMounted,
  onUnmounted,
  ref,
  watch,
  nextTick,
} from "vue";

import debounce from "lodash/debounce";

import CompanyService from "../../api/CompanyService.js";
import UserService from "../../api/UserService.js";

import CompanyToChoose from "../CompanyToChoose.vue";
import ErrorToast from "../../components/ErrorToast.vue";
import WarningToast from "../../components/WarningToast.vue";
import Snackbar from "../../components/Snackbar.vue";

const emit = defineEmits([
  "save-company",
  "complete-step",
  "no-companies-found",
  "companies-found",
]);

const props = defineProps({
  isEmployer: {
    type: Boolean,
    default: false,
  },
});

const companyTitle = computed(() => {
  return props.isEmployer
    ? "Какую компанию вы представляете?"
    : "Где вы сейчас работаете?";
});

const companyDescription = computed(() => {
  return props.isEmployer
    ? "Давайте найдём компанию, которую вы представляете и от имени которой будете размещать вакансии."
    : "Найдите свою компанию, чтобы коллеги и работодатели могли быстрее вас узнать.";
});

const searchQuery = ref("");
const searchResults = ref([]);
const selectedCompanyId = ref(null);

const isSearching = ref(false);
const isUpdating = ref(false);
const isSearchFocused = ref(false);

const successSnackbar = ref(false);
const errorMessage = ref("");
const warningMessage = ref("");

let currentSearchRequestId = 0;
let themeObserver = null;
let successSnackbarTimer = null;
let warningTimer = null;
let errorTimer = null;

const hasSearchQuery = computed(() => {
  return searchQuery.value.trim().length > 0;
});

const selectedCompany = computed(() => {
  if (!selectedCompanyId.value) {
    return null;
  }

  return (
    searchResults.value.find(
      (company) => company.company_id === selectedCompanyId.value
    ) ?? null
  );
});

const resultsCountText = computed(() => {
  const count = searchResults.value.length;
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  let word = "компаний";

  if (lastTwoDigits < 11 || lastTwoDigits > 14) {
    if (lastDigit === 1) {
      word = "компания";
    } else if (lastDigit >= 2 && lastDigit <= 4) {
      word = "компании";
    }
  }

  return `${count} ${word}`;
});

const clearMessages = () => {
  errorMessage.value = "";
  warningMessage.value = "";
};

const showWarning = (message) => {
  window.clearTimeout(warningTimer);

  warningMessage.value = "";
  nextTick(() => {
    warningMessage.value = message;
  });

  warningTimer = window.setTimeout(() => {
    warningMessage.value = "";
  }, 3500);
};

const showError = (message) => {
  window.clearTimeout(errorTimer);

  errorMessage.value = "";
  nextTick(() => {
    errorMessage.value = message;
  });

  errorTimer = window.setTimeout(() => {
    errorMessage.value = "";
  }, 4500);
};

const showSuccessSnackbar = () => {
  window.clearTimeout(successSnackbarTimer);

  successSnackbar.value = false;

  nextTick(() => {
    successSnackbar.value = true;
  });

  successSnackbarTimer = window.setTimeout(() => {
    successSnackbar.value = false;
  }, 2500);
};

const normalizeCompanies = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.companies)) {
    return response.companies;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
};

const rawSearchCompanies = async () => {
  const normalizedQuery = searchQuery.value.trim();
  const requestId = ++currentSearchRequestId;

  if (!normalizedQuery) {
    searchResults.value = [];
    isSearching.value = false;
    return;
  }

  isSearching.value = true;
  errorMessage.value = "";

  try {
    const response = await CompanyService.searchCompanies(normalizedQuery);

    // Игнорируем устаревший ответ, если пользователь уже ввёл новый запрос.
    if (requestId !== currentSearchRequestId) {
      return;
    }

    searchResults.value = normalizeCompanies(response);

    console.log("📡 Найденные компании:", searchResults.value);
  } catch (error) {
    if (requestId !== currentSearchRequestId) {
      return;
    }

    searchResults.value = [];

    console.error(
      "❌ Ошибка загрузки компаний:",
      error?.response?.data || error?.message || error
    );

    showError("Не удалось выполнить поиск. Попробуйте ещё раз.");
  } finally {
    if (requestId === currentSearchRequestId) {
      isSearching.value = false;
    }
  }
};

const searchCompanies = debounce(rawSearchCompanies, 350);

const handleSearchInput = () => {
  clearMessages();

  selectedCompanyId.value = null;

  const normalizedQuery = searchQuery.value.trim();

  if (!normalizedQuery) {
    currentSearchRequestId += 1;
    searchCompanies.cancel();

    searchResults.value = [];
    isSearching.value = false;

    return;
  }

  isSearching.value = true;
  searchCompanies();
};

const clearSearch = () => {
  currentSearchRequestId += 1;
  searchCompanies.cancel();

  searchQuery.value = "";
  searchResults.value = [];
  selectedCompanyId.value = null;
  isSearching.value = false;

  clearMessages();

  focusSearch();
};

const focusSearch = async () => {
  await nextTick();

  const input = document.querySelector(
    ".choose-company-container .search-input"
  );

  input?.focus();
};

const toggleCompanySelection = (companyId) => {
  clearMessages();

  selectedCompanyId.value =
    selectedCompanyId.value === companyId
      ? null
      : companyId;
};

const clearSelection = () => {
  selectedCompanyId.value = null;
  focusSearch();
};

const updateUserCompany = async () => {
  if (isUpdating.value) {
    return;
  }

  if (!selectedCompanyId.value) {
    showWarning("Пожалуйста, выберите компанию перед продолжением.");
    return false;
  }

  const company = selectedCompany.value;

  isUpdating.value = true;
  clearMessages();

  try {
    await UserService.updateUserCompany(
      selectedCompanyId.value,
      company?.name ?? ""
    );

    showSuccessSnackbar();

    emit("save-company", {
      companyId: selectedCompanyId.value,
      companyName: company?.name ?? "",
      company,
    });

    return true;
  } catch (error) {
    console.error(
      "❌ Не удалось обновить компанию:",
      error?.response?.data || error?.message || error
    );

    showError("Не удалось сохранить компанию. Попробуйте позже.");

    return false;
  } finally {
    isUpdating.value = false;
  }
};

const skipCompanyStep = () => {
  if (isUpdating.value) {
    return;
  }

  selectedCompanyId.value = null;

  emit("save-company", {
    skipped: true,
  });
};

const handleNextStep = () => {
  updateUserCompany();
};

watch(isUpdating, (isBlocking) => {
  document.body.classList.toggle("loading", isBlocking);
});

watch(
  () => searchResults.value.length,
  (resultsCount) => {
    if (!hasSearchQuery.value || isSearching.value) {
      return;
    }

    if (resultsCount > 0) {
      emit("companies-found", resultsCount);
    } else {
      emit("no-companies-found");
    }
  }
);

onMounted(() => {
  document.addEventListener("next-step", handleNextStep);

  // Оставляем observer, если в будущем понадобятся реакции
  // компонента на переключение темы.
  themeObserver = new MutationObserver(() => {
    // Иконки теперь SVG и автоматически наследуют цвет из CSS.
  });

  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });

  console.log("🟢 ChooseCompany подписан на next-step");
});

onUnmounted(() => {
  document.removeEventListener("next-step", handleNextStep);

  searchCompanies.cancel();
  currentSearchRequestId += 1;

  themeObserver?.disconnect();
  themeObserver = null;

  window.clearTimeout(successSnackbarTimer);
  window.clearTimeout(warningTimer);
  window.clearTimeout(errorTimer);

  document.body.classList.remove("loading");

  console.log("🔴 ChooseCompany отписан от next-step");
});

defineExpose({
  updateUserCompany,
  skipCompanyStep,
});
</script>

<style scoped>
.choose-company-container {
  --accent: #2887f7;
  --accent-hover: #1675e8;
  --accent-soft: rgba(40, 135, 247, 0.12);
  --accent-border: rgba(40, 135, 247, 0.32);

  --surface-primary: rgba(255, 255, 255, 0.055);
  --surface-secondary: rgba(255, 255, 255, 0.035);
  --surface-hover: rgba(255, 255, 255, 0.075);
  --surface-selected: rgba(40, 135, 247, 0.11);

  --border-primary: rgba(255, 255, 255, 0.1);
  --border-secondary: rgba(255, 255, 255, 0.065);

  --text-primary: rgba(255, 255, 255, 0.96);
  --text-secondary: rgba(255, 255, 255, 0.62);
  --text-tertiary: rgba(255, 255, 255, 0.4);

  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  position: relative;
}

[data-theme="light"] .choose-company-container {
  --surface-primary: #ffffff;
  --surface-secondary: #f7f9fc;
  --surface-hover: #f2f6fb;
  --surface-selected: rgba(40, 135, 247, 0.08);

  --border-primary: rgba(26, 43, 68, 0.11);
  --border-secondary: rgba(26, 43, 68, 0.07);

  --text-primary: #172033;
  --text-secondary: #657086;
  --text-tertiary: #929caf;
}

.company-content {
  width: 100%;
  max-width: 960px;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

/* Header */

.company-header {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  margin-bottom: 30px;
}

.header-icon {
  width: 54px;
  height: 54px;
  flex: 0 0 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent);
  background:
    linear-gradient(
      145deg,
      rgba(40, 135, 247, 0.18),
      rgba(40, 135, 247, 0.07)
    );
  border: 1px solid rgba(40, 135, 247, 0.2);
  border-radius: 17px;
  box-shadow:
    0 10px 30px rgba(40, 135, 247, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.header-icon svg {
  width: 28px;
  height: 28px;
}

.header-text {
  min-width: 0;
}

.company-title {
  margin: 1px 0 7px;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 26px;
  line-height: 1.2;
  color: var(--text-primary);
  letter-spacing: -0.45px;
}

.company-description {
  max-width: 590px;
  margin: 0;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  line-height: 1.55;
  color: var(--text-secondary);
}

/* Search */

.search-section {
  width: 100%;
  max-width: 650px;
  margin-bottom: 22px;
}

.search-bar {
  min-height: 56px;
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 0 17px;
  color: var(--text-tertiary);
  background: var(--surface-primary);
  border: 1px solid var(--border-primary);
  border-radius: 18px;
  box-shadow:
    0 8px 28px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.035);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease,
    transform 0.2s ease;
}

.search-bar:hover {
  background: var(--surface-hover);
  border-color: rgba(40, 135, 247, 0.18);
}

.search-bar.focused {
  color: var(--accent);
  border-color: rgba(40, 135, 247, 0.68);
  box-shadow:
    0 0 0 4px rgba(40, 135, 247, 0.11),
    0 12px 32px rgba(0, 0, 0, 0.1);
}

.search-icon {
  width: 21px;
  height: 21px;
  flex: 0 0 21px;
  transition: color 0.2s ease;
}

.search-input {
  min-width: 0;
  flex: 1;
  padding: 0;
  background: transparent;
  border: none;
  outline: none;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 15px;
  line-height: 1;
  color: var(--text-primary);
  caret-color: var(--accent);
}

.search-input::placeholder {
  color: var(--text-tertiary);
}

.clear-search-button {
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: var(--text-secondary);
  background: var(--surface-secondary);
  border: 1px solid var(--border-secondary);
  border-radius: 50%;
  cursor: pointer;
  transition:
    color 0.18s ease,
    background 0.18s ease,
    transform 0.18s ease;
}

.clear-search-button:hover {
  color: var(--text-primary);
  background: var(--surface-hover);
  transform: scale(1.05);
}

.clear-search-button svg {
  width: 15px;
  height: 15px;
}

.search-spinner {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  border: 2px solid rgba(40, 135, 247, 0.22);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.72s linear infinite;
}

.search-hint {
  margin: 9px 3px 0;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  line-height: 1.4;
  color: var(--text-tertiary);
}

/* Selected company */

.selected-company-banner {
  width: 100%;
  max-width: 650px;
  min-height: 64px;
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 11px 14px;
  margin-bottom: 20px;
  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.15),
      rgba(40, 135, 247, 0.07)
    );
  border: 1px solid var(--accent-border);
  border-radius: 17px;
}

.selected-company-check {
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: var(--accent);
  border-radius: 50%;
  box-shadow: 0 7px 17px rgba(40, 135, 247, 0.27);
}

.selected-company-check svg {
  width: 19px;
  height: 19px;
}

.selected-company-info {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.selected-company-label {
  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  line-height: 1.3;
  color: var(--text-secondary);
}

.selected-company-name {
  overflow: hidden;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;
  line-height: 1.4;
  color: var(--text-primary);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.selected-company-remove {
  padding: 8px 10px;
  color: var(--accent);
  background: transparent;
  border: none;
  border-radius: 9px;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.18s ease;
}

.selected-company-remove:hover {
  background: rgba(40, 135, 247, 0.11);
}

/* Main states */

.results-container {
  width: 100%;
  flex: 1;
  position: relative;
}

.initial-state,
.empty-state {
  min-height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 650px;
  padding: 35px 24px 45px;
  text-align: center;
  background:
    radial-gradient(
      circle at 50% 25%,
      rgba(40, 135, 247, 0.07),
      transparent 48%
    );
  border: 1px dashed var(--border-primary);
  border-radius: 24px;
}

.initial-illustration {
  width: 118px;
  height: 98px;
  position: relative;
  margin-bottom: 22px;
}

.illustration-building {
  position: absolute;
  display: grid;
  gap: 7px;
  padding: 13px 11px;
  border: 1px solid var(--border-primary);
  box-shadow: 0 15px 34px rgba(0, 0, 0, 0.1);
}

.illustration-building span {
  display: block;
  background: rgba(40, 135, 247, 0.2);
  border-radius: 2px;
}

.building-back {
  left: 13px;
  top: 11px;
  width: 52px;
  height: 66px;
  grid-template-columns: repeat(2, 1fr);
  background: var(--surface-secondary);
  border-radius: 12px 12px 5px 5px;
  transform: rotate(-4deg);
}

.building-front {
  right: 10px;
  bottom: 5px;
  width: 59px;
  height: 78px;
  grid-template-columns: repeat(2, 1fr);
  background: var(--surface-primary);
  border-radius: 14px 14px 5px 5px;
  transform: rotate(3deg);
}

.illustration-search {
  width: 43px;
  height: 43px;
  position: absolute;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: var(--accent);
  border: 5px solid var(--surface-primary);
  border-radius: 50%;
  box-shadow: 0 9px 21px rgba(40, 135, 247, 0.3);
}

.illustration-search svg {
  width: 20px;
  height: 20px;
}

.state-title {
  margin: 0 0 8px;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;
  line-height: 1.3;
  color: var(--text-primary);
}

.state-description {
  max-width: 390px;
  margin: 0 0 22px;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.55;
  color: var(--text-secondary);
}

.secondary-skip-button,
.retry-button {
  min-height: 42px;
  padding: 0 17px;
  border-radius: 13px;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 13px;
  cursor: pointer;
  transition:
    color 0.18s ease,
    background 0.18s ease,
    border-color 0.18s ease,
    transform 0.18s ease;
}

.secondary-skip-button {
  color: var(--text-secondary);
  background: transparent;
  border: 1px solid var(--border-primary);
}

.secondary-skip-button:hover {
  color: var(--text-primary);
  background: var(--surface-hover);
  border-color: rgba(40, 135, 247, 0.24);
}

.retry-button {
  color: #ffffff;
  background: var(--accent);
  border: 1px solid var(--accent);
  box-shadow: 0 8px 18px rgba(40, 135, 247, 0.22);
}

.retry-button:hover {
  background: var(--accent-hover);
  border-color: var(--accent-hover);
  transform: translateY(-1px);
}

/* Results */

.search-loading-state,
.search-results-state {
  width: 100%;
}

.results-heading {
  min-height: 43px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  margin-bottom: 15px;
}

.results-title {
  margin: 0 0 3px;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 15px;
  color: var(--text-primary);
}

.results-subtitle {
  margin: 0;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  color: var(--text-tertiary);
}

.searching-dots {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-right: 5px;
}

.searching-dots span {
  width: 5px;
  height: 5px;
  background: var(--accent);
  border-radius: 50%;
  animation: searching-dot 1.1s infinite ease-in-out;
}

.searching-dots span:nth-child(2) {
  animation-delay: 0.14s;
}

.searching-dots span:nth-child(3) {
  animation-delay: 0.28s;
}

.company-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

/* Skeleton */

.company-skeleton {
  min-height: 144px;
  display: flex;
  align-items: flex-start;
  gap: 13px;
  padding: 17px;
  overflow: hidden;
  position: relative;
  background: var(--surface-secondary);
  border: 1px solid var(--border-secondary);
  border-radius: 19px;
}

.skeleton-block {
  overflow: hidden;
  position: relative;
  background: var(--border-primary);
}

.skeleton-block::after {
  content: "";
  position: absolute;
  inset: 0;
  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
  transform: translateX(-100%);
  animation: shimmer 1.4s infinite;
}

[data-theme="light"] .skeleton-block::after {
  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.75),
      transparent
    );
}

.skeleton-logo {
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  border-radius: 14px;
}

.skeleton-content {
  min-width: 0;
  flex: 1;
  padding-top: 3px;
}

.skeleton-title {
  width: 72%;
  height: 13px;
  margin-bottom: 13px;
  border-radius: 5px;
}

.skeleton-line {
  height: 9px;
  border-radius: 4px;
}

.skeleton-line-wide {
  width: 92%;
  margin-bottom: 9px;
}

.skeleton-line-short {
  width: 57%;
}

/* Empty */

.empty-illustration {
  width: 106px;
  height: 106px;
  margin-bottom: 17px;
}

.empty-illustration svg {
  width: 100%;
  height: 100%;
}

.empty-building {
  fill: var(--surface-primary);
  stroke: var(--border-primary);
  stroke-width: 2;
}

.empty-lines {
  stroke: var(--text-tertiary);
  stroke-width: 3;
  stroke-linecap: round;
}

.empty-search-circle {
  fill: var(--surface-primary);
  stroke: var(--accent);
  stroke-width: 4;
}

.empty-search-line {
  stroke: var(--accent);
  stroke-width: 5;
  stroke-linecap: round;
}

.empty-minus {
  stroke: var(--text-secondary);
  stroke-width: 3;
  stroke-linecap: round;
}

.empty-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

/* Overlay */

.loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(7, 12, 22, 0.58);
  backdrop-filter: blur(9px);
  -webkit-backdrop-filter: blur(9px);
}

.saving-card {
  min-width: 310px;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 22px;
  background: rgba(28, 34, 47, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 20px;
  box-shadow: 0 24px 65px rgba(0, 0, 0, 0.3);
}

[data-theme="light"] .saving-card {
  background: rgba(255, 255, 255, 0.97);
  border-color: rgba(20, 38, 65, 0.1);
}

.saving-spinner {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border: 4px solid rgba(40, 135, 247, 0.2);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

.saving-content {
  min-width: 0;
}

.saving-title {
  margin: 0 0 4px;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 15px;
  color: var(--text-primary);
}

.saving-subtitle {
  margin: 0;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  color: var(--text-secondary);
}

/* Transitions */

.content-fade-enter-active,
.content-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.content-fade-enter-from {
  opacity: 0;
  transform: translateY(7px);
}

.content-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.selected-company-enter-active,
.selected-company-leave-active {
  overflow: hidden;
  transition:
    opacity 0.22s ease,
    transform 0.22s ease,
    max-height 0.25s ease,
    margin 0.25s ease;
}

.selected-company-enter-from,
.selected-company-leave-to {
  max-height: 0;
  margin-bottom: 0;
  opacity: 0;
  transform: translateY(-7px);
}

.selected-company-enter-to,
.selected-company-leave-from {
  max-height: 80px;
}

.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.2s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

/* Animations */

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

@keyframes searching-dot {
  0%,
  60%,
  100% {
    opacity: 0.3;
    transform: translateY(0);
  }

  30% {
    opacity: 1;
    transform: translateY(-4px);
  }
}

/* Tablet */

@media (max-width: 900px) {
  .company-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Mobile */

@media (max-width: 768px) {
  .choose-company-container {
    width: 100%;
    padding: 0;
  }

  .company-content {
    max-width: none;
  }

  .company-header {
    gap: 13px;
    margin-bottom: 23px;
  }

  .header-icon {
    width: 46px;
    height: 46px;
    flex-basis: 46px;
    border-radius: 15px;
  }

  .header-icon svg {
    width: 24px;
    height: 24px;
  }

  .company-title {
    margin-top: 0;
    font-size: 21px;
    letter-spacing: -0.3px;
  }

  .company-description {
    font-size: 13px;
    line-height: 1.48;
  }

  .search-section,
  .selected-company-banner,
  .initial-state,
  .empty-state {
    max-width: none;
  }

  .search-bar {
    min-height: 53px;
    padding: 0 15px;
    border-radius: 16px;
  }

  .search-input {
    font-size: 16px;
  }

  .search-hint {
    padding: 0 3px;
    font-size: 11px;
  }

  .selected-company-banner {
    padding: 10px 12px;
    border-radius: 15px;
  }

  .selected-company-remove {
    padding: 7px;
  }

  .company-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .initial-state,
  .empty-state {
    min-height: 300px;
    padding: 30px 18px 38px;
    border-radius: 20px;
  }

  .state-title {
    font-size: 17px;
  }

  .state-description {
    font-size: 12px;
  }

  .empty-actions {
    width: 100%;
    flex-direction: column;
  }

  .retry-button,
  .empty-actions .secondary-skip-button {
    width: 100%;
    max-width: 280px;
  }

  .saving-card {
    width: 100%;
    min-width: 0;
    max-width: 360px;
  }
}

@media (max-width: 420px) {
  .company-header {
    align-items: center;
  }

  .company-description {
    display: none;
  }

  .company-title {
    font-size: 20px;
  }

  .selected-company-name {
    max-width: 155px;
  }

  .initial-illustration {
    transform: scale(0.9);
    margin-bottom: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>

<style>
body.loading {
  overflow: hidden;
}
</style>